import type { TFunction } from 'i18next';
import { KEYMAP, type CommandId, type ToolId } from '@os/engine';
import type { PaletteCommand } from '../palette/commands';
import { READ_ONLY_TOOLS, type EditorController } from './controller';
import { TOOLS } from './Toolbar';
import type { MainMenuEntry } from './main-menu';

/** Other names people type for a tool (FR and EN), on top of its label. */
const TOOL_KEYWORDS: Partial<Record<ToolId, string[]>> = {
  sticky: ['post-it', 'note'],
  shape: ['rectangle', 'ellipse', 'forme'],
  connector: ['line', 'ligne', 'flèche'],
  section: ['frame', 'groupe'],
  table: ['tableau', 'grid'],
  tasklist: ['checklist', 'todo', 'tâches', 'liste'],
};

/** First combo of an action in the shared KEYMAP, shown next to the command. */
export const shortcutOf = (action: ToolId | CommandId): string | undefined => Object.entries(KEYMAP).find(([, a]) => a === action)?.[0];

/** The editor's own palette commands: its tools, then board commands (export, zoom, settings, help). */
export function editorCommands({
  t, controller, readOnly, withComments, onExport, onSettings, onHelp, sections = [], onGoToSection,
}: {
  t: TFunction;
  controller: EditorController;
  readOnly: boolean;
  withComments: boolean;
  onExport?: () => void;
  onSettings: () => void;
  onHelp: () => void;
  /** The board's sections in reading order: one "Go to section" each (P5e). */
  sections?: readonly { id: string; name: string }[];
  onGoToSection?: (id: string) => void;
}): PaletteCommand[] {
  const tools: ToolId[] = [...TOOLS.filter((tool) => !readOnly || READ_ONLY_TOOLS.has(tool)), ...(withComments ? (['comment'] as const) : [])];
  const command = (id: CommandId): PaletteCommand => ({ id, label: t(`commands.${id}`), group: 'board', shortcut: shortcutOf(id), run: () => controller.runCommand(id) });
  return [
    ...tools.map((tool): PaletteCommand => ({
      id: `tool:${tool}`,
      label: t(`tools.${tool}`),
      group: 'tools',
      keywords: TOOL_KEYWORDS[tool],
      shortcut: shortcutOf(tool),
      run: () => {
        controller.pickTool(tool);
        controller.focus();
      },
    })),
    ...(onExport ? [{ id: 'export', label: t('export.open'), group: 'board' as const, keywords: ['png', 'pdf', 'json'], run: onExport }] : []),
    command('zoomFit'),
    command('zoomReset'),
    ...(onGoToSection
      ? sections.map((s): PaletteCommand => ({
          id: `section:${s.id}`,
          label: t('palette.goToSection', { name: s.name || t('editor.sectionPlaceholder') }),
          group: 'board',
          keywords: ['section'],
          run: () => onGoToSection(s.id),
        }))
      : []),
    ...(readOnly ? [] : [command('undo'), command('redo')]),
    { id: 'settings', label: t('boardSettings.open'), group: 'board', run: onSettings },
    { id: 'help', label: t('toolbar.help'), group: 'app', keywords: ['shortcuts', 'raccourcis', 'aide'], shortcut: '?', run: onHelp },
  ];
}

/** Menu leaves the palette already offers under another id, or not as an action. */
const MENU_SKIP = (id: string) => id.startsWith('goToSection:') || id === 'noSections' || id === 'shortcuts';

/**
 * Every enabled action of the board's main menu as a palette command (P15c, backlog §24: ⌘K reaches all of them),
 * its submenus' names as keywords ("arrange" finds "Align left"). Disabled leaves (editing on a read-only board) stay out.
 */
export function menuCommands(entries: readonly MainMenuEntry[], path: readonly string[] = []): PaletteCommand[] {
  return entries.flatMap((e): PaletteCommand[] => {
    if ('separator' in e) return [];
    if ('children' in e) return menuCommands(e.children, [...path, e.label]);
    if (e.disabled || MENU_SKIP(e.id)) return [];
    return [{ id: e.id, label: e.label, group: 'board', keywords: [...path], shortcut: e.shortcut, run: e.run }];
  });
}

/** The first command of each id (the editor's own, then what the page registered, then the menu's). */
export const uniqueById = (commands: readonly PaletteCommand[]): PaletteCommand[] => {
  const seen = new Set<string>();
  return commands.filter((c) => !seen.has(c.id) && !!seen.add(c.id));
};

/** The enabled main menu actions the palette lacks (P15c): empty when ⌘K reaches everything the menu does. */
export function paletteCoverage(palette: readonly PaletteCommand[], menu: readonly MainMenuEntry[]): { missing: string[] } {
  const ids = new Set(palette.map((c) => c.id));
  const leaves = (entries: readonly MainMenuEntry[]): string[] =>
    entries.flatMap((e) => ('separator' in e ? [] : 'children' in e ? leaves(e.children) : e.disabled || MENU_SKIP(e.id) ? [] : [e.id]));
  return { missing: [...new Set(leaves(menu))].filter((id) => !ids.has(id)) };
}
